import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CloudOff } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useMyBusiness } from "../hooks/useBusiness";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { BusinessSetupPage } from "../pages/BusinessSetupPage";
import { isSetupInProgress } from "../utils/setupFlag";

/**
 * Gate for the "/" route: while the authenticated user has no business — or is
 * mid-way through the setup flow — render the full-screen BusinessSetupPage
 * INSTEAD of the AppLayout chrome, so the dark onboarding design owns the
 * viewport (no double header / nested scroll).
 *
 * "No business" is only the definitive 403/404 answer from the API. Any other
 * failure (5xx, or a network error which rejects without a status) is an
 * outage — showing the create-business screen then would strand an existing
 * user on a form they must not submit, so we show a retry panel instead.
 *
 * The setup-in-progress flag covers the create transition: the moment the POST
 * succeeds the business appears in the query cache, and without the flag this
 * gate would swap in the app shell and unmount the setup page together with
 * the onboarding-mode modal it is about to show. The flag is set right before
 * the create request fires and cleared when the modal sequence finishes (see
 * BusinessSetupPage) or on logout (AuthContext).
 *
 * Platform admins skip the gate — they operate above the tenant layer and get
 * the regular AppLayout (HomePage then forwards them to /platform).
 */
export function SetupGate() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    data: business,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useMyBusiness();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isPlatformAdmin = user?.is_platform_admin ?? false;
  const definitiveNoBusiness =
    isError && (error?.status === 403 || error?.status === 404);
  const noBusiness = !business && (!isError || definitiveNoBusiness);
  const outage = !business && isError && !definitiveNoBusiness;

  if (outage && !isPlatformAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30">
            <CloudOff size={22} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t("onboarding.setupError.title")}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {t("onboarding.setupError.body")}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="mt-6 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:from-blue-400 hover:to-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isRefetching
              ? t("onboarding.setupError.retrying")
              : t("onboarding.setupError.retry")}
          </button>
        </div>
      </div>
    );
  }

  if ((noBusiness || isSetupInProgress()) && !isPlatformAdmin) {
    return <BusinessSetupPage />;
  }

  return <Outlet />;
}
